import {useAccountExitGuard} from '../hooks/useUnsavedChanges';
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {can} from "../lib/permissions";
import {downloadAuthorized} from "../lib/download";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRefData } from "../context/RefDataContext";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/StatusBadge";
import { VEHICLE_STATUS, fmtNum, fmtEUR, labelize } from "../lib/format";
import { normalizeBusinessForm } from "../lib/businessValidation";
import { Car, Plus, Search, Download, LayoutGrid, List, Copy, Pencil, Loader2, Gauge, MapPin, Archive, Printer } from "lucide-react";
import { printTable } from "../lib/print";
import VehicleRequests from "../components/VehicleRequests";
import EmptyState from "../components/EmptyState";
import useRememberedState from "../hooks/useRememberedState";
import useIsPhone from "../hooks/useIsPhone";
import ListSkeleton from "../components/ListSkeleton";
import VehicleColor from "../components/VehicleColor";
import { VEHICLE_COLORS, baseColor, colorSwatch } from "../lib/vehicleColors";

function VehiclesEmpty({ filtered, onReset, onCreate, archived }) {
  if (archived && !filtered) return <EmptyState icon={Archive} title="Nessun veicolo archiviato" description="I veicoli archiviati compaiono qui e si possono ripristinare dalla loro scheda." />;
  return filtered
    ? <EmptyState icon={Search} title="Nessun veicolo trovato" description="Nessun veicolo corrisponde a ricerca e filtri." action={<Button variant="outline" size="sm" onClick={onReset}>Azzera filtri</Button>} />
    : <EmptyState icon={Car} title="Aggiungi il primo veicolo" description="Targa, modello e sede bastano per iniziare." action={onCreate && <Button size="sm" onClick={onCreate}><Plus className="mr-2 h-4 w-4" />Nuovo veicolo</Button>} />;
}

const STATUS_OPTS = Object.keys(VEHICLE_STATUS);
const FUEL_OPTS = ["benzina", "diesel", "ibrido", "elettrico", "gpl", "metano"];
const CAT_OPTS = ["utilitaria", "berlina", "suv", "furgone", "commerciale", "station_wagon"];
const GEAR_OPTS = ["manuale", "automatico"];
const OWN_OPTS = ["proprietà", "leasing", "noleggio"];

const VFIELDS = [
  ["internal_id", "ID interno", "text"], ["plate", "Targa", "text"], ["brand", "Marca", "text"],
  ["model", "Modello", "text"], ["version", "Versione/Allestimento", "text"],
  ["category", "Categoria", "select", CAT_OPTS], ["vin", "Numero telaio", "text"],
  ["fuel", "Alimentazione", "select", FUEL_OPTS], ["displacement", "Cilindrata (cc)", "number"],
  ["power_hp", "Potenza (CV)", "number"], ["gearbox", "Cambio", "select", GEAR_OPTS],
  ["color", "Colore", "color"], ["registration_year", "Anno immatricolazione", "number"],
  ["purchase_date", "Data acquisto", "date"], ["purchase_price", "Prezzo acquisto", "number"],
  ["current_value", "Valore attuale", "number"], ["mileage_current", "Chilometraggio", "number"],
  ["location_id", "Sede", "ref-loc"], ["status", "Stato", "select", STATUS_OPTS],
  ["ownership", "Proprietà", "select", OWN_OPTS], ["supplier_id", "Fornitore", "ref-sup"],
  ["contract_end", "Scadenza contratto", "date"], ["keys_count", "Numero chiavi", "number"],
];
const VREQUIRED = ["plate", "brand", "model"];
// The editor shows every field, grouped under headings: most used first.
const VSECTIONS = [
  ["Identificazione", ["plate", "brand", "model", "color", "version", "category", "internal_id", "vin"]],
  ["Stato e sede", ["status", "location_id", "mileage_current", "keys_count"]],
  ["Caratteristiche tecniche", ["fuel", "gearbox", "displacement", "power_hp", "registration_year"]],
  ["Acquisto e proprietà", ["ownership", "supplier_id", "purchase_date", "purchase_price", "current_value", "contract_end"]],
];

export default function Vehicles() {
  const { user } = useAuth();
  const refData = useRefData();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chosenView, setView] = useRememberedState("vehicles:layout", "table");
  // On a phone the cards fit the screen; the table would scroll sideways.
  const phone = useIsPhone(), view = phone ? "cards" : chosenView;
  const [q, setQ] = useRememberedState("vehicles:search", "");
  const [fStatus, setFStatus] = useRememberedState("vehicles:status", "all");
  const [fFuel, setFFuel] = useRememberedState("vehicles:fuel", "all");
  const [fCat, setFCat] = useRememberedState("vehicles:category", "all");
  const statusCounts = useMemo(() => vehicles.reduce((counts, v) => ({ ...counts, [v.status]: (counts[v.status] || 0) + 1 }), {}), [vehicles]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const accessExitConfirmation=useAccountExitGuard({dirty:dialogOpen,busy:saving});

  const [archivedView, setArchivedView] = useState(false);
  const canSeeArchive = ["vehicles.archive", "vehicles.delete", "vehicles.request_lifecycle"].some((key) => can(user, key));
  // Archived vehicles are only consulted and restored: no edits, copies or new vehicles from that list.
  const canWrite = can(user,"vehicles.manage") && !archivedView;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/vehicles", archivedView ? { params: { archived: true } } : undefined); setVehicles(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [archivedView]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return vehicles.filter((v) =>
      (!q || [v.plate, v.brand, v.model, v.vin].some((x) => String(x || "").toLowerCase().includes(ql))) &&
      (fStatus === "all" || v.status === fStatus) &&
      (fFuel === "all" || v.fuel === fFuel) &&
      (fCat === "all" || v.category === fCat)
    );
  }, [vehicles, q, fStatus, fFuel, fCat]);

  const openCreate = () => { setEditing(null); setForm({ status: "disponibile", keys_count: 2, ...(user.active_site_id!=="all"?{location_id:user.active_site_id}:{}) }); setDialogOpen(true); };
  const openEdit = (v, e) => {
    e?.stopPropagation();
    const editable = { ...v };
    delete editable.stored_status;
    delete editable.availability_reason;
    setEditing(v);
    setForm({ ...editable, status: v.stored_status ?? v.status });
    setDialogOpen(true);
  };

  const save = async () => {
    let payload;
    try {
      payload = normalizeBusinessForm(form, VFIELDS.filter(([key])=>!(editing&&key==="location_id")).map(([key, label, type]) => ({
        key, label, type, required: ["plate", "brand", "model"].includes(key),
      })), "vehicles");
      // Older hand-written colours are saved as their base colour ("Grigio met." → Grigio).
      if ("color" in payload) payload.color = baseColor(payload.color)?.name || null;
      delete payload.stored_status;
      delete payload.availability_reason;
      delete payload.is_archived;
      if(editing)delete payload.location_id;
    } catch (e) { toast.error(e.message); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/vehicles/${editing.id}`, payload,{headers:{'If-Match':editing.updated_at||''}}); toast.success("Veicolo aggiornato"); }
      else { await api.post("/vehicles", payload); toast.success("Veicolo creato"); }
      setDialogOpen(false); await load(); refData.reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const duplicate = async (v, e) => {
    e?.stopPropagation();
    try { await api.post(`/vehicles/${v.id}/duplicate`); toast.success("Veicolo duplicato"); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const locName = (id) => refData.locations.find((l) => l.id === id)?.name || "—";
  // The list as filtered on screen, the same columns as the table.
  const printVehicles = () => printTable({
    title: archivedView ? "Veicoli archiviati" : "Veicoli", user,
    columns: ["Targa", "Veicolo", "Colore", "Categoria", "Alimentazione", "Sede", { label: "Km", align: "right" }, "Stato"].map((c) => typeof c === "string" ? { label: c } : c),
    rows: filtered.map((v) => [v.plate, [v.brand, v.model, v.version].filter(Boolean).join(" "), { text: v.color || "—", swatch: colorSwatch(v.color) },
      labelize(v.category), labelize(v.fuel), locName(v.location_id), fmtNum(v.mileage_current), VEHICLE_STATUS[v.status]?.label || labelize(v.status)]),
  });
  const setF = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  return (
    <div className="space-y-5 animate-fade-up">{accessExitConfirmation}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="icon-tile h-11 w-11" aria-hidden="true"><Car className="h-6 w-6" /></div>
          <div><h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Veicoli</h1><p className="text-sm text-muted-foreground">{vehicles.length} {vehicles.length === 1 ? (archivedView ? "veicolo archiviato" : "veicolo in flotta") : (archivedView ? "veicoli archiviati" : "veicoli in flotta")}</p></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSeeArchive && <Button variant={archivedView ? "secondary" : "outline"} size="sm" aria-pressed={archivedView} onClick={() => setArchivedView((value) => !value)} data-testid="vehicles-archived-toggle" aria-label="Archiviati" title="Archiviati"><Archive className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Archiviati</span></Button>}
          {!phone && <div className="flex rounded-lg border border-border p-0.5">
            <Button variant={view === "table" ? "secondary" : "ghost"} size="icon" className="h-8 w-8" onClick={() => setView("table")} data-testid="view-table"><List className="h-4 w-4" /></Button>
            <Button variant={view === "cards" ? "secondary" : "ghost"} size="icon" className="h-8 w-8" onClick={() => setView("cards")} data-testid="view-cards"><LayoutGrid className="h-4 w-4" /></Button>
          </div>}
          {can(user,"export")&&<Button disabled={loading||!filtered.length} variant="outline" size="sm" onClick={printVehicles} data-testid="print-vehicles" aria-label="Stampa" title="Stampa"><Printer className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">Stampa</span></Button>}
          {can(user,"export")&&!archivedView&&<Button disabled={loading||!filtered.length} variant="outline" size="sm" onClick={() => downloadAuthorized("/exports/vehicles","veicoli.csv",{ids:filtered.map(row=>row.id).join(",")}).catch(e=>toast.error(apiError(e)))} aria-label="Scarica CSV" title="Scarica CSV"><Download className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">CSV</span></Button>}
          {canWrite && <Button size="sm" disabled={user.active_site_id==="all"} title={user.active_site_id==="all"?"Seleziona una singola sede per aggiungere il veicolo":undefined} onClick={openCreate} data-testid="add-vehicle-btn"><Plus className="mr-2 h-4 w-4" /> Nuovo veicolo</Button>}
        </div>
      </div>

      {!archivedView && <VehicleRequests />}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca targa, marca, modello, telaio…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="vehicle-search" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="w-[190px]" data-testid="filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tutti gli stati ({vehicles.length})</SelectItem>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{VEHICLE_STATUS[s].label} ({statusCounts[s] || 0})</SelectItem>)}</SelectContent></Select>
        
        <Select value={fFuel} onValueChange={setFFuel}><SelectTrigger className="w-[140px]"><SelectValue placeholder="Alimentazione" /></SelectTrigger><SelectContent><SelectItem value="all">Alimentazione</SelectItem>{FUEL_OPTS.map((f) => <SelectItem key={f} value={f}>{labelize(f)}</SelectItem>)}</SelectContent></Select>
        <Select value={fCat} onValueChange={setFCat}><SelectTrigger className="w-[140px]"><SelectValue placeholder="Categoria" /></SelectTrigger><SelectContent><SelectItem value="all">Categoria</SelectItem>{CAT_OPTS.map((c) => <SelectItem key={c} value={c}>{labelize(c)}</SelectItem>)}</SelectContent></Select>
      </div>

      {loading ? (
        <Card className="overflow-hidden border-border bg-card"><ListSkeleton rows={5} label="Caricamento veicoli…" /></Card>
      ) : view === "table" ? (
        <Card className="overflow-hidden border-border bg-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead>Targa</TableHead><TableHead>Veicolo</TableHead><TableHead className="text-center">Colore</TableHead><TableHead>Categoria</TableHead>
                <TableHead>Alimentazione</TableHead><TableHead>Sede</TableHead><TableHead>Km</TableHead>
                <TableHead>Stato</TableHead>{canWrite && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer border-border" onClick={() => navigate(`/veicoli/${v.id}`)} data-testid={`vehicle-row-${v.id}`}>
                    <TableCell className="font-mono font-semibold text-sky-300">{v.plate}</TableCell>
                    <TableCell><span className="font-medium">{v.brand} {v.model}</span><span className="block text-xs text-muted-foreground">{v.version}</span></TableCell>
                    <TableCell className="text-center align-middle">{v.color ? <VehicleColor color={v.color} size="lg" className="align-middle" /> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{labelize(v.category)}</TableCell>
                    <TableCell>{labelize(v.fuel)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{locName(v.location_id)}</TableCell>
                    <TableCell>{fmtNum(v.mileage_current)}</TableCell>
                    <TableCell className="max-w-[240px]">
                      <StatusBadge status={v.status} kind="vehicle" />
                      {v.availability_reason && <p className="mt-1 text-xs text-muted-foreground">{v.availability_reason}</p>}
                    </TableCell>
                    {canWrite && (
                      <TableCell className="text-right"><div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => openEdit(v, e)} data-testid={`edit-vehicle-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => duplicate(v, e)} data-testid={`dup-vehicle-${v.id}`}><Copy className="h-4 w-4" /></Button>
                      </div></TableCell>
                    )}
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow className="hover:bg-transparent"><TableCell colSpan={8} className="p-0"><VehiclesEmpty archived={archivedView} filtered={!!q || fStatus !== "all" || fFuel !== "all" || fCat !== "all"} onReset={() => { setQ(""); setFStatus("all"); setFFuel("all"); setFCat("all"); }} onCreate={canWrite && user.active_site_id !== "all" ? openCreate : null} /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((v) => (
            <Card key={v.id} className="cursor-pointer border-border bg-card p-4 transition-colors hover:border-primary/40" onClick={() => navigate(`/veicoli/${v.id}`)} data-testid={`vehicle-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-mono text-lg font-bold text-primary">{v.plate}</p><p className="text-sm font-medium">{v.brand} {v.model}</p><p className="text-xs text-muted-foreground">{v.version}</p></div>
                <StatusBadge status={v.status} kind="vehicle" />
              </div>
              {v.availability_reason && <p className="mt-2 text-xs text-muted-foreground">{v.availability_reason}</p>}
              {v.color && <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">Colore <VehicleColor color={v.color} size="lg" /></p>}
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> {v.mileage_current == null ? "—" : `${fmtNum(v.mileage_current)} km`}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {locName(v.location_id)}</span>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <div className="col-span-full"><VehiclesEmpty archived={archivedView} filtered={!!q || fStatus !== "all" || fFuel !== "all" || fCat !== "all"} onReset={() => { setQ(""); setFStatus("all"); setFFuel("all"); setFCat("all"); }} onCreate={canWrite && user.active_site_id !== "all" ? openCreate : null} /></div>}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica veicolo" : "Nuovo veicolo"}</DialogTitle><DialogDescription>Dati anagrafici e operativi del veicolo. Salva per confermare.</DialogDescription></DialogHeader>
          {editing?.availability_reason && (
            <p className="text-sm text-muted-foreground">
              Disponibilità attuale: {VEHICLE_STATUS[editing.status]?.label || labelize(editing.status)} · {editing.availability_reason}.
              {" "}La disponibilità tiene conto delle operazioni registrate.
            </p>
          )}
          <div className="space-y-6 py-2">
            {VSECTIONS.map(([section, keys]) => (
              <section key={section} aria-label={section}>
                <h3 className="mb-3 border-b border-border pb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{section}</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {keys.map((fieldKey) => VFIELDS.find(([key]) => key === fieldKey)).filter(([key]) => !(editing && key === "location_id")).map(([key, label, type, opts]) => (
                  <div key={key}>
                    <Label className="mb-1.5 block text-sm">{label}{VREQUIRED.includes(key) && <span className="text-destructive" aria-hidden="true"> *</span>}</Label>
                    {type === "select" ? (
                      <Select value={form[key] || ""} onValueChange={(val) => setF(key, val)}><SelectTrigger data-testid={`vfield-${key}`}><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{key === "status" ? VEHICLE_STATUS[o].label : labelize(o)}</SelectItem>)}</SelectContent></Select>
                    ) : type === "color" ? (
                      <Select value={baseColor(form[key])?.name || "none"} onValueChange={(val) => setF(key, val === "none" ? "" : val)}>
                        <SelectTrigger data-testid={`vfield-${key}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non indicato</SelectItem>
                          {VEHICLE_COLORS.map((c) => <SelectItem key={c.name} value={c.name}><span className="flex items-center gap-2"><VehicleColor color={c.name} size="sm" />{c.name}</span></SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : type === "ref-loc" ? (
                      <Select disabled value={form[key] || ""} onValueChange={(val) => setF(key, val)}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{refData.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
                    ) : type === "ref-sup" ? (
                      <Select value={form[key] || ""} onValueChange={(val) => setF(key, val)}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{refData.suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
                    ) : (
                      <Input type={type} min={type === "number" ? 0 : undefined} step={type === "number" ? "any" : undefined} value={form[key] ?? ""} onChange={(e) => setF(key, e.target.value)} data-testid={`vfield-${key}`} />
                    )}
                  </div>
                  ))}
                </div>
              </section>
            ))}
            <section aria-label="Note">
              <h3 className="mb-3 border-b border-border pb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</h3>
              <Label htmlFor="vehicle-notes" className="sr-only">Note interne</Label><Textarea id="vehicle-notes" placeholder="Note interne" value={form.notes || ""} onChange={(e) => setF("notes", e.target.value)} rows={2} />
            </section>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-vehicle-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
